import { ConfigurePromotionReadinessScoreWriteBackBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WorkflowPromotionReadinessScoreWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-score-write-back/promotion-readiness-score-write-back';
import React, { useCallback } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WorkflowPlaces } from 'apps/sigma2-ui/src/components/workflows/WorkflowPlaces';
import { WriteBackChartHopAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-score-write-back/sources/chart-hop';
import { WriteBackChartHopPlaceToWrite } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-score-write-back/sources/chart-hop';
import { WriteBackChartHopStrategy } from 'libs/end-to-end/src/types/databases/workflows/promotion-readiness-score-write-back/sources/chart-hop';
import { ConfigureWriteBackChartHopAuthMethod } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackChartHopAuthMethod';

export interface ConfigurePromotionReadinessScoreWriteBackDetailsProps
  extends ConfigurePromotionReadinessScoreWriteBackBaseProps {
  details: WorkflowPromotionReadinessScoreWriteBackDetails | null;
  updateDetails: (
    details: WorkflowPromotionReadinessScoreWriteBackDetails | null,
  ) => void;
}

export const ConfigurePromotionReadinessScoreWriteBackDetails = ({
  details,
  updateDetails,
  listDataSetsPattern,
  listSecretsPattern,
  organizationId,
}: ConfigurePromotionReadinessScoreWriteBackDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowPromotionReadinessScoreWriteBackDetails>) => {
      updateDetails({
        type: 'promotionReadinessScoreWriteBack',
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'promotionReadinessScoreWriteBack',
            strategy: {
              type: 'chartHop',
            },
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateDetailsPartial({
        mode,
      });
    },
    [updateDetailsPartial],
  );

  const updateStrategy = useCallback(
    (strategy: WriteBackChartHopStrategy | undefined) => {
      updateDetailsPartial({
        strategy: strategy,
      });
    },
    [updateDetailsPartial],
  );

  const updateMinReadinessRating = useCallback(
    (minReadinessRating: number | undefined) => {
      updateDetailsPartial({
        minReadinessRating:
          minReadinessRating !== undefined && isNaN(minReadinessRating)
            ? undefined
            : minReadinessRating,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateAuthStrategy = useCallback(
    (authMethod: WriteBackChartHopAuthMethod | undefined) => {
      updateStrategy({
        ...details?.strategy,
        type: 'chartHop',
        authMethod,
      });
    },
    [details?.strategy, updateStrategy],
  );

  const updatePlacesToWrite = useCallback(
    (placesToWrite: WriteBackChartHopPlaceToWrite[]) => {
      updateStrategy({
        ...details?.strategy,
        type: 'chartHop',
        placesToWrite,
      });
    },
    [details?.strategy, updateStrategy],
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure Promotion Readiness Score Write Back
      </h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable the promotion readiness score write back
      </S2Checkbox>

      {details && (
        <>
          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
          />

          <ConfigureWriteBackChartHopAuthMethod
            authMethod={details.strategy?.authMethod ?? null}
            updateAuthMethod={(authMethod) => {
              updateAuthStrategy(authMethod ?? undefined);
            }}
            listDataSetsPattern={listDataSetsPattern}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
          />

          <WorkflowPlaces
            placesToWrite={details.strategy?.placesToWrite ?? []}
            updatePlacesToWrite={updatePlacesToWrite}
            placeToWriteComponent={({
              setNewPlaceToWrite,
              newPlaceToWrite,
            }) => {
              return (
                <>
                  <div className="space-y-4 p-4 bg-white rounded shadow">
                    <label className="block text-sm font-medium text-gray-700">
                      Org ID
                    </label>
                    <input
                      className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                      type="text"
                      value={newPlaceToWrite?.orgId ?? ''}
                      onChange={(e) => {
                        setNewPlaceToWrite({
                          ...newPlaceToWrite,
                          type: 'customFields',
                          orgId: e.target.value,
                        });
                      }}
                    />

                    <label className="block text-sm font-medium text-gray-700">
                      Custom Field for Score
                    </label>
                    <input
                      className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                      type="text"
                      value={
                        newPlaceToWrite?.customFieldNameForPromotionReadinessScore ??
                        ''
                      }
                      onChange={(e) => {
                        setNewPlaceToWrite({
                          ...newPlaceToWrite,
                          type: 'customFields',
                          customFieldNameForPromotionReadinessScore:
                            e.target.value,
                        });
                      }}
                    />

                    <label className="block text-sm font-medium text-gray-700">
                      Custom Field for Job Title Group
                    </label>
                    <input
                      className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                      type="text"
                      value={
                        newPlaceToWrite?.customFieldNameForPromotionReadinessScoreJobGroup ??
                        ''
                      }
                      onChange={(e) => {
                        setNewPlaceToWrite({
                          ...newPlaceToWrite,
                          type: 'customFields',
                          customFieldNameForPromotionReadinessScoreJobGroup:
                            e.target.value,
                        });
                      }}
                    />
                  </div>
                </>
              );
            }}
          />

          <label className="space-y-1 flex flex-col">
            <span>Minimum readiness rating</span>

            <input
              type="number"
              value={details.minReadinessRating ?? ''}
              onChange={(e) => {
                updateMinReadinessRating(e.target.valueAsNumber);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <label className="space-y-1 flex flex-col">
            <span>Number of Samples To Send</span>

            <input
              type="number"
              value={details.numberOfSamplesToSend ?? ''}
              onChange={(e) => {
                updateNumberOfSamplesToSend(e.target.valueAsNumber);
              }}
              className="form-input border rounded p-2"
            />
          </label>
        </>
      )}
    </div>
  );
};
